import { useI18n } from '../contexts/I18nContext';
import { motion } from 'framer-motion';
import { ShieldCheck, Cpu, Users, Layers, Headset } from 'lucide-react';

export default function WhyChooseSection() {
  const { t } = useI18n();

  const icons = [Layers, Cpu, Users, ShieldCheck, Headset];
  const colors = ['#9FFFCB', '#5B3FD0', '#B18AE0', '#00A6FB', '#FF9FDF'];

  return (
    <section className="py-20 bg-gradient-to-b from-[#F8F5FF] to-white relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#5B3FD0]/10 bg-white shadow-sm mb-6"
          >
            <div className="w-2 h-2 bg-[#5B3FD0] rounded-full animate-pulse"></div>
            <span className="text-sm font-bold text-[#5B3FD0] uppercase tracking-wider">
              Why Choose Us
            </span>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-extrabold text-[#2C2C2C] mb-4"
          >
            {t.whyChoose.title}
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#707070] leading-relaxed"
          >
            {t.whyChoose.intro}
          </motion.p>
        </div>

        {/* Feature Grid: 5 Items (Top row 3, Bottom row 2 centered) */}
        <div className="flex flex-wrap justify-center gap-6 max-w-6xl mx-auto">
          {t.whyChoose.points.map((point, index) => {
            const Icon = icons[index % icons.length];
            const color = colors[index % colors.length];

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-3xl p-6 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-black/5 w-full md:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)] flex flex-col items-center text-center group relative overflow-hidden"
              >
                {/* Subtle Background Icon */}
                <div className="absolute top-0 right-0 -mr-6 -mt-6 opacity-[0.02] transform group-hover:scale-110 group-hover:opacity-[0.04] transition-all duration-700 pointer-events-none">
                  <Icon size={160} />
                </div>

                {/* Icon */}
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:-translate-y-2 transition-transform duration-300 relative z-10"
                  style={{ backgroundColor: `${color}15`, color: color === '#9FFFCB' ? '#2C2C2C' : color }}
                >
                  <Icon size={32} />
                </div>

                <h3 className="text-xl font-bold text-[#2C2C2C] mb-3 relative z-10">
                  {point.title}
                </h3>
                <p className="text-[#707070] text-sm md:text-base leading-relaxed relative z-10">
                  {point.description}
                </p>
              </motion.div>
            );
          })}
        </div>

      </div>
    </section>
  );
}
